export interface ContentToken {
  text: string
  isWord: boolean
  lower: string
}

const SINGLE_LETTER_WORDS = new Set(['i', 'a'])

function isApostrophe(ch: string): boolean {
  return ch === "'" || ch === '\u2019' || ch === '\u2018'
}

/** True when the token is worth showing as a clickable dictionary word. */
export function isClickableWord(text: string): boolean {
  const letters = text.replace(/[''\u2018\u2019]/g, '')
  if (letters.length >= 2) return true
  return SINGLE_LETTER_WORDS.has(text.toLowerCase())
}

/**
 * Splits text into clickable word tokens and plain spans.
 * Contractions (Let's, don't) stay one token; orphan letters like "s" are not clickable.
 */
export function tokenizeContent(content: string): ContentToken[] {
  const tokens: ContentToken[] = []
  let i = 0

  while (i < content.length) {
    if (/[a-zA-Z]/.test(content[i])) {
      const start = i
      while (i < content.length) {
        const ch = content[i]
        if (/[a-zA-Z]/.test(ch)) {
          i++
        } else if (isApostrophe(ch) && i + 1 < content.length && /[a-zA-Z]/.test(content[i + 1])) {
          i++
        } else {
          break
        }
      }
      const text = content.slice(start, i)
      const clickable = isClickableWord(text)
      tokens.push({
        text,
        isWord: clickable,
        lower: clickable ? text.toLowerCase() : '',
      })
    } else {
      const start = i
      while (i < content.length && !/[a-zA-Z]/.test(content[i])) i++
      tokens.push({ text: content.slice(start, i), isWord: false, lower: '' })
    }
  }

  return tokens
}

/** Strip markdown bold markers for context lookup and encounter scanning. */
export function stripStoryMarkdown(content: string): string {
  return content.replace(/\*\*/g, '')
}

/** Collect lowercase surface forms from text that match the user's known-forms set. */
export function findVocabWordsInText(content: string, knownForms: Iterable<string>): string[] {
  const formSet = new Set([...knownForms].map((w) => w.toLowerCase()))
  const plain = stripStoryMarkdown(content)
  const found = new Set<string>()
  for (const token of tokenizeContent(plain)) {
    if (token.isWord && formSet.has(token.lower)) {
      found.add(token.lower)
    }
  }
  return [...found]
}
